import { createEffect, sample } from 'effector';
import type { Project } from '@finsim/model';
import { toastShown } from '../entities/notifications';
import { $project, commandRejected, editCommitted } from '../entities/project';
import { projectLoadFailed } from '../pages/project';

/*
 * App-wide wiring that doesn't belong to one page: error toasts and performance marks.
 */

/** The item or goal a rejected edit would make invalid, named from the first issue's path. */
function invalidName(
  project: Project | null,
  issues: readonly { path: PropertyKey[] }[],
): string | null {
  const [root, key] = issues[0]?.path ?? [];
  if (root === 'items' && typeof key === 'string') return project?.items[key]?.name ?? null;
  if (root === 'goals' && typeof key === 'number') return project?.goals[key]?.name ?? null;
  return null;
}

sample({
  clock: commandRejected,
  source: $project,
  fn: (project, { issues }) => {
    const name = invalidName(project, issues);
    return name === null
      ? { message: 'errors.commandRejected' as const, tone: 'error' as const }
      : {
          message: 'errors.commandRejectedFor' as const,
          params: { name },
          tone: 'error' as const,
        };
  },
  target: toastShown,
});

sample({
  clock: projectLoadFailed,
  fn: (code) => ({
    message:
      code === 'notFound'
        ? ('errors.projectNotFound' as const)
        : code === 'futureVersion'
          ? ('errors.futureVersion' as const)
          : ('errors.projectInvalid' as const),
    tone: 'error' as const,
  }),
  target: toastShown,
});

/** Start of the "edit → chart painted" measurement (ended in the chart canvas). */
const markEditFx = createEffect(() => {
  performance.clearMarks('finsim:edit');
  performance.mark('finsim:edit');
});
sample({ clock: editCommitted, target: markEditFx });
